import React from 'react';
import {
  Alert, Box, Button, Paper, Stack, Tab, Tabs, TextField,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import { FWorkbenchShell } from '../workbench';
import FPlanningJourney from '../core/FPlanningJourney';
import { fetchPlanningJourneyPhases } from '../core/planningJourney.cache';

import HistoricalImportMasterDetail from './HistoricalImportMasterDetail';
import HistoricalManualEntryGrid from './HistoricalManualEntryGrid';
import HistoricalWorkbookPanel from './HistoricalWorkbookPanel';

import {
  advanceHistoricalPhase,
  fetchActivePlanningCycle,
  fetchHistoricalFacts,
  fetchHistoricalImports,
  generateHistoricalMatrix,
  saveHistoricalFacts,
} from './bieHistorical.apis';

import {
  HistoricalEntryDraft,
  HistoricalFactRow,
  HistoricalImportRow,
} from './bieHistorical.models';

type State = {
  tab: string;
  loading: boolean;
  loadingFacts: boolean;
  error: string;
  success: string;

  activeCycle: any;
  planningcycleid: string;
  phases: any[];

  imports: HistoricalImportRow[];
  selectedImport: HistoricalImportRow | null;
  facts: HistoricalFactRow[];

  metricCode: string;
  fiscalYear: string;
  manualRows: HistoricalEntryDraft[];
  completionNotes: string;
};

class BieHistoricalWorkbench extends React.Component<any,State> {
  state:State = {
    tab: 'imports',
    loading: false,
    loadingFacts: false,
    error: '',
    success: '',

    activeCycle: {},
    planningcycleid: '',
    phases: [],

    imports: [],
    selectedImport: null,
    facts: [],

    metricCode: '',
    fiscalYear: '',
    manualRows: [],
    completionNotes: '',
  };

  componentDidMount() {
    this.initialize();
  }

  private initialize = async () => {
    this.setState({ loading:true, error:'' });

    try {
      const [activeCycle, phases] = await Promise.all([fetchActivePlanningCycle(), fetchPlanningJourneyPhases()]);
      this.setState({ activeCycle, phases });
      //const phases = await fetchPlanningJourneyPhases();
      this.setState({ phases });
      await this.loadImports();
    } catch(error:any) {
      this.setState({ error:error?.message || String(error) });
    } finally {
      this.setState({ loading:false });
    }
  };

  private loadImports = async () => {
    const imports = await fetchHistoricalImports({
      planningcycleid: this.state.activeCycle?.rid ?? null,
      pageSize: 50,
    });

    this.setState({ imports });
  };

  private selectImport = async (
    selectedImport: HistoricalImportRow | null,
  ) => {
    this.setState({
      selectedImport,
      facts: [],
      loadingFacts: Boolean(selectedImport),
    });

    if (!selectedImport) return;

    try {
      const facts = await fetchHistoricalFacts({
        importid: selectedImport.rid,
        trackid: selectedImport.trackid || null,
        pageSize: 500,
      });

      this.setState({ facts });
    } catch(error:any) {
      this.setState({ error:error?.message || String(error) });
    } finally {
      this.setState({ loadingFacts:false });
    }
  };

  private generateMatrix = async () => {
    this.setState({ loading:true, error:'', success:'' });

    try {
      const rows = await generateHistoricalMatrix({
        metricCode:this.state.metricCode,
        fiscalYear:this.state.fiscalYear,
      });

      this.setState({
        manualRows: rows.map((r:any, index:number) => ({
          ...r,
          rid: r.rid || `NEW-${index + 1}`,
          __new: true,
          __dirty: false,
        })),
        success: `${rows.length} expected fact row(s) generated.`,
      });
    } catch(error:any) {
      this.setState({ error:error?.message || String(error) });
    } finally {
      this.setState({ loading:false });
    }
  };

  private saveManualRows = async () => {
    this.setState({ loading:true, error:'', success:'' });

    try {
      const records = this.state.manualRows.map((r:any) => ({
        mtc:r.metriccode,
        prc:r.productcode,
        pec:r.periodcode,
        qty:Number(r.quantity || 0),
        upr:Number(r.unitprice || 0),
        amt:Number(r.amount || 0),
        rmk:r.remarks || '',
      }));

      await saveHistoricalFacts(records);
      this.setState({ success:'Historical facts saved.' });
      await this.loadImports();
    } catch(error:any) {
      this.setState({ error:error?.message || String(error) });
    } finally {
      this.setState({ loading:false });
    }
  };

  private completeHistorical = async () => {
    if (!this.state.planningcycleid) {
      this.setState({ error:'Select the Planning Cycle before completing Historical Data.' });
      return;
    }

    this.setState({ loading:true, error:'', success:'' });

    try {
      await advanceHistoricalPhase(
        Number(this.state.planningcycleid),
        this.state.completionNotes,
      );

      this.setState({
        success:'Historical Data completed. Planning Cycle advanced to Forecast & Baseline.',
      });
    } catch(error:any) {
      this.setState({ error:error?.message || String(error) });
    } finally {
      this.setState({ loading:false });
    }
  };

  private renderTab = () => {
    switch(this.state.tab) {
      case 'manual':
        return (
          <HistoricalManualEntryGrid
            rows={this.state.manualRows}
            loading={this.state.loading}
            metricCode={this.state.metricCode}
            fiscalYear={this.state.fiscalYear}
            onMetricChange={(metricCode) => this.setState({ metricCode })}
            onFiscalYearChange={(fiscalYear) => this.setState({ fiscalYear })}
            onGenerate={this.generateMatrix}
            onRowsChange={(manualRows) => this.setState({ manualRows })}
            onSave={this.saveManualRows}
          />
        );

      case 'workbook':
        return (
          <HistoricalWorkbookPanel
            metricCode={this.state.metricCode}
            fiscalYear={this.state.fiscalYear}
            onCompleted={async () => {
              this.setState({ success:'Workbook import completed.' });
              await this.loadImports();
            }}
          />
        );

      case 'coverage':
        return (
          <Alert severity="info">
            Connect the existing Integrity Check, coverage, and missing-period components here.
          </Alert>
        );

      default:
        return (
          <HistoricalImportMasterDetail
            imports={this.state.imports}
            facts={this.state.facts}
            loadingImports={this.state.loading}
            loadingFacts={this.state.loadingFacts}
            selectedImport={this.state.selectedImport}
            onSelectImport={this.selectImport}
          />
        );
    }
  };

  render() {
    return (
      <FWorkbenchShell
        title="BIE Historical Data"
        subtitle="Prepare approved enterprise facts before forecasting and simulation."
        icon={<HistoryIcon />}
        loading={this.state.loading}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.loadImports}
        headerExtension={
          <FPlanningJourney
            phases={this.state.phases}
            currentPhaseCode="HISTORICAL_DATA"
          />
        }
      >
        <Stack spacing={2}>
          <Paper elevation={1} sx={{ p:2 }}>
            <Stack direction={{ xs:'column', md:'row' }} spacing={2}>
              {/* <TextField
                size="small"
                label="Planning Cycle ID"
                value={this.state.planningcycleid}
                onChange={(e) => this.setState({ planningcycleid:e.target.value })}
                sx={{ minWidth:220 }}
              /> */}
              <TextField
                size="small"
                label="Planning Cycle"
                value={
                  this.state.activeCycle?.name ||
                  this.state.activeCycle?.code ||
                  ''
                }
                helperText={
                  this.state.activeCycle
                    ? `Current phase: ${this.state.activeCycle.phasecode}`
                    : 'Activate a Planning Cycle before continuing.'
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{ minWidth: 280 }}
              />

              <TextField
                size="small"
                label="Completion Notes"
                value={this.state.completionNotes}
                onChange={(e) => this.setState({ completionNotes:e.target.value })}
                fullWidth
              />

              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={this.completeHistorical}
                disabled={!this.state.planningcycleid}
                sx={{ whiteSpace:'nowrap' }}
              >
                Proceed to Forecasting
              </Button>
            </Stack>
          </Paper>

          <Paper elevation={1}>
            <Tabs
              value={this.state.tab}
              onChange={(_, tab) => this.setState({ tab })}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="imports" label="Import History" />
              <Tab value="manual" label="Manual Entry" />
              <Tab value="workbook" label="Excel Import" />
              <Tab value="coverage" label="Coverage & Gaps" />
            </Tabs>
          </Paper>

          <Box>{this.renderTab()}</Box>
        </Stack>
      </FWorkbenchShell>
    );
  }
}

export default BieHistoricalWorkbench;
